<script setup>
/* global axios */
// Siteyi tarayarak asistan egitme.
//
// NEDEN: Chatwoot'un Belgeler ekrani adres basina TEK sayfa aliyor.
// Musteri "sitemi egit" demek istiyor; otuz adresi tek tek yapistirmasi
// gercekci degil.
//
// Adresleri biz buluyoruz (site haritasi, yoksa baglantilari gezerek),
// hangi sayfanin ise yaradigina model karar veriyor, son sozu musteri
// soyluyor: onaylamadan hicbir belge olusmuyor. Tarama belge kotasini
// harciyor, sessizce otuz kayit acmak dogru olmazdi.
import { computed, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  assistantId: { type: [Number, String], required: true },
});

const emit = defineEmits(['eklendi']);

const { t } = useI18n();
const route = useRoute();

const YOKLAMA_MS = 3000;

const taban = computed(
  () => `/api/v1/accounts/${route.params.accountId}/site-tarama`
);

const acik = ref(false);
const adres = ref('');
const azami = ref(20);

const taraniyor = ref(false);
const ekleniyor = ref(false);
const hata = ref('');
const sonuc = ref(null);
const secili = ref(new Set());

let zamanlayici = null;

const durdur = () => {
  if (zamanlayici) clearTimeout(zamanlayici);
  zamanlayici = null;
};

onUnmounted(durdur);

const secililer = computed(() =>
  (sonuc.value?.sayfalar || []).filter(s => secili.value.has(s.adres))
);

const secimiCevir = sayfaAdresi => {
  const yeni = new Set(secili.value);
  if (yeni.has(sayfaAdresi)) yeni.delete(sayfaAdresi);
  else yeni.add(sayfaAdresi);
  secili.value = yeni;
};

const yokla = async isId => {
  try {
    const { data } = await axios.get(`${taban.value}/${isId}`, {
      params: { assistant_id: props.assistantId },
    });

    if (data.durum === 'calisiyor') {
      zamanlayici = setTimeout(() => yokla(isId), YOKLAMA_MS);
      return;
    }

    taraniyor.value = false;
    if (data.durum === 'hata') {
      hata.value = data.hata || t('OMNUP_SITE_TARAMA.HATA');
      return;
    }

    sonuc.value = data.sonuc;
    // Model zaten eledi; bulunanlarin tamami isaretli gelsin, musteri
    // istemediklerini kaldirsin.
    secili.value = new Set((data.sonuc?.sayfalar || []).map(s => s.adres));
  } catch (e) {
    taraniyor.value = false;
    hata.value = e?.response?.data?.error || t('OMNUP_SITE_TARAMA.HATA');
  }
};

const tara = async () => {
  if (!adres.value.trim()) return;

  durdur();
  taraniyor.value = true;
  hata.value = '';
  sonuc.value = null;
  secili.value = new Set();

  try {
    const { data } = await axios.post(taban.value, {
      assistant_id: props.assistantId,
      adres: adres.value,
      azami: azami.value,
    });
    zamanlayici = setTimeout(() => yokla(data.is_id), YOKLAMA_MS);
  } catch (e) {
    taraniyor.value = false;
    hata.value = e?.response?.data?.error || t('OMNUP_SITE_TARAMA.HATA');
  }
};

const ekle = async () => {
  if (!secililer.value.length) return;

  ekleniyor.value = true;
  hata.value = '';
  try {
    const { data } = await axios.post(`${taban.value}/ekle`, {
      assistant_id: props.assistantId,
      baslangic: sonuc.value?.baslangic || adres.value,
      adresler: secililer.value.map(s => s.adres),
    });
    useAlert(
      t('OMNUP_SITE_TARAMA.EKLENDI', { sayi: (data.eklenen || []).length })
    );
    sonuc.value = null;
    secili.value = new Set();
    emit('eklendi');
  } catch (e) {
    hata.value = e?.response?.data?.error || t('OMNUP_SITE_TARAMA.HATA');
  } finally {
    ekleniyor.value = false;
  }
};
</script>

<template>
  <div class="mb-4 rounded-xl border border-solid border-n-weak bg-n-alpha-1">
    <button
      type="button"
      class="flex items-center justify-between w-full gap-3 px-4 py-3 bg-transparent border-0 cursor-pointer"
      @click="acik = !acik"
    >
      <span class="flex items-center gap-2 min-w-0">
        <span class="i-lucide-globe size-4 text-n-brand shrink-0" />
        <span class="flex flex-col items-start min-w-0">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('OMNUP_SITE_TARAMA.BASLIK') }}
          </span>
          <span class="text-xs text-start text-n-slate-11">
            {{ t('OMNUP_SITE_TARAMA.ALT_BASLIK') }}
          </span>
        </span>
      </span>
      <span
        class="size-4 shrink-0 text-n-slate-11"
        :class="acik ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
      />
    </button>

    <div v-if="acik" class="flex flex-col gap-3 px-4 pb-4">
      <div class="flex flex-wrap items-end gap-2">
        <label class="flex flex-col flex-1 gap-1 min-w-64">
          <span class="text-xs font-medium text-n-slate-12">
            {{ t('OMNUP_SITE_TARAMA.ADRES') }}
          </span>
          <input
            v-model="adres"
            type="text"
            class="!mb-0 text-sm"
            placeholder="https://magazam.com"
          />
        </label>

        <label class="flex flex-col gap-1">
          <span class="text-xs font-medium text-n-slate-12">
            {{ t('OMNUP_SITE_TARAMA.AZAMI') }}
          </span>
          <select v-model.number="azami" class="!mb-0 text-sm">
            <option :value="10">10</option>
            <option :value="20">20</option>
            <option :value="30">30</option>
            <option :value="50">50</option>
          </select>
        </label>

        <Button
          type="button"
          size="sm"
          variant="solid"
          :label="t('OMNUP_SITE_TARAMA.TARA')"
          :is-loading="taraniyor"
          :disabled="taraniyor || !adres.trim()"
          @click="tara"
        />
      </div>

      <div
        v-if="taraniyor"
        class="flex items-center gap-2 px-3 py-2 text-xs rounded-lg bg-n-alpha-2 text-n-slate-11"
      >
        <Spinner class="text-n-brand size-4" />
        {{ t('OMNUP_SITE_TARAMA.TARANIYOR') }}
      </div>

      <p v-if="hata" class="text-sm text-n-ruby-11">{{ hata }}</p>

      <template v-if="sonuc">
        <p class="text-xs text-n-slate-11">
          {{
            t('OMNUP_SITE_TARAMA.OZET', {
              bulunan: sonuc.bulunan,
              secilen: sonuc.sayfalar.length,
            })
          }}
        </p>

        <ul class="flex flex-col gap-1 m-0 list-none max-h-72 overflow-y-auto">
          <li
            v-for="sayfa in sonuc.sayfalar"
            :key="sayfa.adres"
            class="flex items-start gap-2 px-2 py-1.5 rounded-lg hover:bg-n-alpha-1"
          >
            <input
              :id="`omnup-sayfa-${sayfa.adres}`"
              type="checkbox"
              class="mt-1 shrink-0"
              :checked="secili.has(sayfa.adres)"
              @change="secimiCevir(sayfa.adres)"
            />
            <label
              :for="`omnup-sayfa-${sayfa.adres}`"
              class="flex flex-col min-w-0 cursor-pointer"
            >
              <span v-if="sayfa.neden" class="text-xs text-n-slate-12">
                {{ sayfa.neden }}
              </span>
              <span class="text-xs break-all text-n-slate-10">
                {{ sayfa.adres }}
              </span>
            </label>
          </li>
        </ul>

        <div class="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="solid"
            :label="
              t('OMNUP_SITE_TARAMA.EKLE', { sayi: secililer.length })
            "
            :is-loading="ekleniyor"
            :disabled="ekleniyor || !secililer.length"
            @click="ekle"
          />
          <span class="text-xs text-n-slate-10">
            {{ t('OMNUP_SITE_TARAMA.EKLE_NOT') }}
          </span>
        </div>
      </template>
    </div>
  </div>
</template>
